import {
  ApiPerformanceReview,
  ApiPerformanceReviewParticipantChartFilter,
  ApiPerformanceReviewParticipantChartPayload,
} from "@/types/performance-review.types.ts";
import { PerformanceReviewInsightChart } from "./PerformanceReviewInsightChart.tsx";
import {
  useParticipantChart,
  useParticipantChartSearch,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { useChartSetting } from "@/pages/talent/performance-reviews/view/use-chart-setting.hook.tsx";
import { Box, Skeleton, Stack } from "@skailer-com/uikit";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { PerformanceReviewInsightChartTable } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewInsightChartTable.tsx";
import { useRef, useState } from "react";
import { useDebounce } from "react-use";
import { useSearchParams } from "react-router-dom";
import { Search } from "@/components/search/Search.tsx";
import { useTranslation } from "react-i18next";

export const PerformanceReviewInsights = ({ review }: { review: ApiPerformanceReview }) => {
  const { t } = useTranslation();
  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const tableRef = useRef<HTMLDivElement | null>(null);

  const {
    filter: { filter },
    filterButton,
  } = useAppFilter<ApiPerformanceReviewParticipantChartFilter>({
    filters: [
      library.page_number,
      library.text,
      library.division_ids,
      library.department_ids,
      library.employee_ids,
    ],
    source: "Performance Review Insights",
  });

  const { setting, settingButton } = useChartSetting({ review: review });

  const { data: chartData, isLoading: isChartDataLoading } = useParticipantChart(review.id, {
    setting: setting,
    filter: {
      ...filter,
      page_number: 1,
      text: undefined,
      employee_ids: undefined,
    },
  } as ApiPerformanceReviewParticipantChartPayload);

  const {
    data: participantData,
    isLoading: isParticipantDataLoading,
    isFetching: isParticipantDataFetching,
  } = useParticipantChartSearch(review.id, { setting: setting, filter: filter });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set(library.text.searchParamName, searchTerm);
        else searchParams.delete(library.text.searchParamName);
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  return (
    <Box className={"pt-10 pb-20"}>
      <Stack gap={"md"}>
        <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"overflow-hidden"}>
          {filterButton({ clearable: true })}
          {settingButton()}
        </Stack>
        {isChartDataLoading && <Skeleton className={"h-[670px]"}></Skeleton>}
        {chartData && (
          <PerformanceReviewInsightChart
            chart={chartData}
            onClickBubble={(employee_ids) => {
              setSearchParams((searchParams) => {
                searchParams.set(library.employee_ids.name, employee_ids.join(","));
                return searchParams;
              });

              requestAnimationFrame(() => {
                tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
          />
        )}
        <Box ref={tableRef}>
          <Search
            value={searchTerm}
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
            placeholder={t("common:search_by_name")}
            className={"w-96"}
          />
          <PerformanceReviewInsightChartTable
            performanceReviewId={review.id}
            items={participantData?.result.items || []}
            config={participantData?.config}
            isLoading={isParticipantDataLoading}
            isFetching={isParticipantDataFetching}
            onPageChange={(page) => {
              setSearchParams((searchParams) => {
                searchParams.set(library.page_number.searchParamName, page.toString());
                return searchParams;
              });
            }}
            metadata={participantData?.result.metadata}
            employeeIds={filter.employee_ids}
            onClearEmployeeFilter={() => {
              setSearchParams((searchParams) => {
                searchParams.delete(library.employee_ids.name);
                return searchParams;
              });
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};
